import {
  Box,
  CircularProgress,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  BusinessOutlined as CompaniesIcon,
  WhereToVote as ActiveIcon,
  ReportProblemOutlined as OverLimitIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {getCompaniesPaged, getCompanyStats} from "../services/system/CompanyService";
import AddCompanyDialog from "../componenets/company/AddCompanyDialog";
import {getSubscriptionPlanName, isOverLimit, getMaxTrainees} from "../enums/SubscriptionPlans";
import moment from "moment";
import {StatusChip, FilterDropdown, Pagination} from "../componenets/ui";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {KPI} from "../componenets/dashboard/coach/KpiRow";
import {useAuth} from "../contexts/AuthContext";

/* ── Constants & helpers ─────────────────────────────────── */
const PAGE_SIZE = 20;
const STATUS_OPTS = [
  {value: "ALL", label: "הכול"},
  {value: "true", label: "פעיל", color: t.green},
  {value: "false", label: "לא פעיל", color: t.red},
];

/* ── Table cell presets (match Clients) ─── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   Companies page
   ═══════════════════════════════════════════════════════════ */
const Companies = () => {
  const navigate = useNavigate();
  // A company group admin may reach this page by URL but has no creation capabilities (it only views
  // its authorized companies via the dashboard / impersonates them) — so the "new company" CTA is hidden.
  const {isCompanyGroupAdmin} = useAuth();
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [newCompanyOpen, setNewCompanyOpen] = useState(false);
  const [stats, setStats] = useState(null);

  const [f, setF] = useState({
    companies: [],
    total: 0,
    totalPages: 0,
    keyword: query.get("keyword") || "",
    enabled: query.get("enabled") != null ? query.get("enabled") === "true" : null,
    overLimit: query.get("overLimit") === "true",
    page: +(query.get("page") || 0),
    loading: true,
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL sync
  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.enabled != null) p.set("enabled", ns.enabled);
    if (ns.overLimit) p.set("overLimit", true);
    if (ns.page > 0) p.set("page", ns.page);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  };

  const set = (patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); };

  // Load stats once
  useEffect(() => { getCompanyStats().then(r => setStats(r.data)).catch(() => {}); }, []);

  // Fetch companies
  useEffect(() => {
    setF(p => ({...p, loading: true}));
    getCompaniesPaged(f.page, PAGE_SIZE, f.keyword || null, f.enabled, f.overLimit)
        .then(r => setF(p => ({
          ...p,
          companies: r.data.content,
          total: r.data.totalElements,
          totalPages: r.data.totalPages,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
  }, [f.page, f.keyword, f.enabled, f.overLimit]);

  const reload = () => {
    setF(p => ({...p, keyword: "", enabled: null, overLimit: false, page: 0, loading: true}));
    setSearchInput("");
    navigate(location.pathname, {replace: true});
  };

  // Status filter value <-> dropdown string ("ALL" | "true" | "false")
  const statusValue = f.enabled == null ? "ALL" : String(f.enabled);
  const onStatusChange = (v) => set({enabled: v === "ALL" ? null : v === "true"});

  const pages = Math.ceil(f.total / PAGE_SIZE);

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
        {/* Dialog */}
        <AddCompanyDialog open={newCompanyOpen} close={() => setNewCompanyOpen(false)} setReload={reload}/>

        {/* ─── Title row (on canvas) ─── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              חברות
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול חברות</Typography>
          </Box>
          {!isCompanyGroupAdmin() && (
          <Box component="button" onClick={() => setNewCompanyOpen(true)} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff",
            height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            חברה חדשה
          </Box>
          )}
        </Box>

        {/* ─── KPI grid ─── */}
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(3, 1fr)"}, gap: "14px", flexShrink: 0}}>
          <KPI
              icon={<CompaniesIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
              value={stats?.total?.toLocaleString() ?? "—"} valueColor={t.text}
              label="סה״כ חברות"
              onClick={() => set({enabled: null, overLimit: false})}
          />
          <KPI
              icon={<ActiveIcon sx={{fontSize: 22}}/>} iconBg={t.greenLight} iconColor={t.green}
              value={stats?.active?.toLocaleString() ?? "—"} valueColor={t.green}
              label="חברות פעילות"
              onClick={() => set({enabled: true, overLimit: false})}
          />
          <KPI
              icon={<OverLimitIcon sx={{fontSize: 22}}/>} iconBg={t.redLight} iconColor={t.red}
              badge="חריגה מהמסלול" badgeBg={t.redLight} badgeColor={t.redText}
              value={stats?.overLimit?.toLocaleString() ?? "—"} valueColor={t.red}
              label="חרגו מהמסלול"
              onClick={() => set({overLimit: true, enabled: null})}
          />
        </Box>

        {/* ─── Table card ─── */}
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>

          {/* Filter bar inside card */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0, direction: "rtl",
          }}>
            {/* Count pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.greenText,
              background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <CompaniesIcon sx={{fontSize: 14}}/>
              {f.total} חברות
            </Box>

            {/* Search */}
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש לפי שם חברה..."
                  sx={{
                    width: 220,
                    padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.white,
                    color: t.text,
                    fontSize: "12px",
                    "& input": {padding: 0},
                    "& input::placeholder": {color: t.t3, opacity: 1},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>

            <FilterDropdown label="סטטוס" value={statusValue} onChange={onStatusChange} options={STATUS_OPTS} minWidth={120}/>

            {/* Over-plan toggle */}
            <Box
                component="button"
                onClick={() => set({overLimit: !f.overLimit})}
                sx={{
                  display: "inline-flex", alignItems: "center", gap: "5px",
                  height: 34, px: "12px", borderRadius: t.rs, cursor: "pointer", fontFamily: "inherit",
                  fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap", transition: "all .14s",
                  border: `1.5px solid ${f.overLimit ? t.red : t.border}`,
                  background: f.overLimit ? t.redLight : t.white,
                  color: f.overLimit ? t.redText : t.t2,
                  "&:hover": {borderColor: t.red, color: t.redText},
                }}
            >
              <OverLimitIcon sx={{fontSize: 15}}/>
              חרגו מהמסלול
            </Box>
          </Box>

          {/* Table area */}
          {f.loading && !f.companies.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !f.companies.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                <CompaniesIcon sx={{fontSize: 48, color: t.border}}/>
                <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין חברות</Typography>
                <Typography sx={{color: t.t3, fontSize: "13px"}}>הוסף חברה חדשה כדי להתחיל</Typography>
              </Box>
          ) : (
              <>
                <TableContainer sx={{overflowY: "auto", maxHeight: 600, direction: "rtl"}}>
                  <Table stickyHeader sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{...th, textAlign: "right"}}>שם החברה</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "15%", display: {xs: "none", md: "table-cell"}}}>מסלול</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "12%", display: {xs: "none", md: "table-cell"}}}>מתאמנים</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "15%", display: {xs: "none", lg: "table-cell"}}}>כתובת</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "12%", display: {xs: "none", md: "table-cell"}}}>תאריך יצירה</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "10%"}}>סטטוס</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.companies.map(company => {
                        // Subscription usage is measured on ACTIVE trainees only — disabled
                        // trainees don't consume a seat, so they never push a company over its plan.
                        const activeCount = company.numberOfActiveTrainees ?? 0;
                        const overLimit = isOverLimit(company.subscriptionPlan, activeCount);
                        const maxT = getMaxTrainees(company.subscriptionPlan);
                        return (
                            <TableRow key={company.id} onClick={() => navigate(`/app/companies/${company.id}`)}
                                      sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}>
                              <TableCell sx={{...td, textAlign: "right"}}>
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                  <Typography sx={{fontSize: "12.5px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    {company.name}
                                  </Typography>
                                  {company.bundleId && (
                                    <Box component="span" sx={{
                                      fontSize: 9.5, fontWeight: 700, padding: "1px 8px", borderRadius: "10px", whiteSpace: "nowrap", lineHeight: 1.6,
                                      background: t.greenLight, color: t.greenText, border: "1px solid rgba(1,187,167,.2)",
                                    }}>
                                      White Label
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                                <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>
                                  {getSubscriptionPlanName(company.subscriptionPlan)}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                <Typography sx={{fontSize: "12.5px", fontWeight: overLimit ? 700 : 500, color: overLimit ? t.redText : t.t2, direction: "ltr"}}>
                                  {activeCount} / {maxT ? maxT : "∞"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "right", display: {xs: "none", lg: "table-cell"}}}>
                                <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                  {company.address || "—"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>
                                  {moment.unix(company.createdDate).format("DD/MM/YY")}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{...td, textAlign: "center"}}>
                                {company.enabled
                                    ? <StatusChip label="פעיל" variant="success" size="small"/>
                                    : <StatusChip label="לא פעיל" variant="error" size="small"/>}
                              </TableCell>
                            </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                )}
              </>
          )}
        </Box>
      </Box>
  );
};

export default Companies;
